import { useState, useEffect, useMemo, useCallback } from 'react';
import { getMyChargerHistory } from '../services/charger-history-service';
import { ChargerAttributes } from '../models/charger-model';
//@ts-ignore
import { SparklineDataPoint } from '../components/graphs/SparklineChart';
import { isSuccessful } from '../services/api-requests';

interface UseHostChargerSparklineDataProps {
  chargers: ChargerAttributes[];
  enabled?: boolean;
}

interface ChargerSparklineData {
  [chargerId: string]: SparklineDataPoint[];
}

export const useHostChargerSparklineData = ({ 
  chargers, 
  enabled = true 
}: UseHostChargerSparklineDataProps) => {
  // Initialize with 7 days of zero data for all chargers
  const initializeSparklineData = (chargerList: ChargerAttributes[]): ChargerSparklineData => {
    const initialData: ChargerSparklineData = {};
    const endDate = new Date();
    
    chargerList.forEach((charger) => {
      if (charger.uuid) {
        initialData[charger.uuid] = Array.from({ length: 7 }, (_, index) => {
          const date = new Date(endDate);
          date.setDate(date.getDate() - (6 - index)); // Start from 6 days ago to today
          return {
            day: date.toISOString().split('T')[0],
            totalKwh: 0,
          };
        });
      }
    });
    
    return initialData;
  };

  const [sparklineData, setSparklineData] = useState<ChargerSparklineData>(() => 
    initializeSparklineData(chargers)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate date range (exactly 7 days: 6 days ago to today)
  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 6); // 6 days ago + today = 7 days total
    
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }, []);

  // Process charger history data into daily aggregates (last 7 days only)
  const processChargerHistory = (chargerHistory: any[], chargerId: string): SparklineDataPoint[] => {
    // Create a map to aggregate kWh by date
    const dailyData = new Map<string, number>();

    // Initialize exactly 7 days with 0 - from 6 days ago to today (UTC day string)
    const today = new Date();
    const dayKeys: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayKey = date.toISOString().split('T')[0];
      dailyData.set(dayKey, 0);
      dayKeys.push(dayKey);
    }
    const validDayKeySet = new Set(dayKeys);

    // Aggregate actual data if it exists
    if (Array.isArray(chargerHistory) && chargerHistory.length > 0) {
      chargerHistory.forEach((history, idx) => {
        const rawDate: string | null = history?.service_date || history?.charging_end_time || history?.charging_start_time || null;
        const totalKwhRaw = history?.total_kwh;
        const totalKwh = typeof totalKwhRaw === 'number' && isFinite(totalKwhRaw) ? totalKwhRaw : Number(totalKwhRaw) || 0;

        if (!rawDate) {
          return;
        }

        const parsed = new Date(rawDate);
        if (isNaN(parsed.getTime())) {
          return;
        }

        const historyDayKey = parsed.toISOString().split('T')[0];
        const inRange = validDayKeySet.has(historyDayKey);

        if (!inRange) {
          return;
        }

        const currentTotal = dailyData.get(historyDayKey) || 0;
        dailyData.set(historyDayKey, currentTotal + totalKwh);
      });
    }

    // Convert to array format sorted by date - ALWAYS return 7 days
    const result = Array.from(dailyData.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, totalKwh]) => ({
        day,
        totalKwh,
      }));

    return result;
  };

  const fetchSparklineData = useCallback(async () => {
    if (!enabled || chargers.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch all charger history
      const response = await getMyChargerHistory();
      
      if (isSuccessful(response.status) && response.payload) {
        const newSparklineData: ChargerSparklineData = {};
        const allHistory = response.payload;

        // Group history by charger
        const historyByCharger = new Map<string, any[]>();
        allHistory.forEach(h => {
          const chargerId = h.chargerId;
          if (chargerId) {
            if (!historyByCharger.has(chargerId)) {
              historyByCharger.set(chargerId, []);
            }
            historyByCharger.get(chargerId)?.push(h);
          }
        });

        // Process each charger's data
        chargers.forEach((charger) => {
          if (charger.uuid) {
            const chargerHistory = historyByCharger.get(charger.uuid) || [];
            newSparklineData[charger.uuid] = processChargerHistory(
              chargerHistory,
              charger.uuid
            );
          }
        });

        // Ensure all displayed chargers have data (7 days with zeros if no actual data)
        chargers.forEach((charger) => {
          if (charger.uuid && !newSparklineData[charger.uuid]) {
            // Create 7 days of zero data
            const endDate = new Date();
            newSparklineData[charger.uuid] = Array.from({ length: 7 }, (_, index) => {
              const date = new Date(endDate);
              date.setDate(date.getDate() - (6 - index)); // Start from 6 days ago to today
              return {
                day: date.toISOString().split('T')[0],
                totalKwh: 0,
              };
            });
          }
        });
        
        setSparklineData(newSparklineData);
      } else {
        throw new Error('Failed to fetch host charger transaction data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [enabled, chargers]);

  useEffect(() => {
    // Re-initialize sparkline data when chargers change
    setSparklineData(initializeSparklineData(chargers));
    if (enabled && chargers.length > 0) {
      fetchSparklineData();
    }
  }, [chargers.length, enabled, fetchSparklineData]);

  return {
    sparklineData,
    loading,
    error,
    refetch: fetchSparklineData,
  };
};

